import { SearchCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { Alert, AlertAction, AlertDescription, AlertTitle } from 'shared/components/ui/alert'
import { formatDayTime } from 'shared/lib/utils/date'
import { Hint } from 'shared/components/section'

/**
 * «Посылка нашлась у нас» — the counter's find of a Parcel support had declared
 * lost (ADR-0024, ticket 43). The operator met it at their Point some other way
 * than a Bag's opening (behind the shelf, brought in): found by the parcel search,
 * one press says it is here. Of this Point it goes straight back onto the shelf; of
 * another, it is set aside here for the driver. Either way its partner is asked —
 * deliver or return — and it is not handed over until the answer; the panel says
 * so — a notice over the card, its one button in it. The find call is injected (`find`) so it is the stubbed tRPC seam in tests.
 */

export type FoundAtCounterAnswer = RouterOutputs['foundParcels']['AtPoint']

async function findViaTrpc(parcelId: string): Promise<FoundAtCounterAnswer> {
	return trpcClient.foundParcels.AtPoint.mutate({ parcelId })
}

export interface FoundAtCounterPanelProps {
	parcelId: string
	/** Test seam: the find. Defaults to the real tRPC client. */
	find?: (parcelId: string) => Promise<FoundAtCounterAnswer>
	/** Called after the find, so the detail can refetch. */
	onFound?: (answer: FoundAtCounterAnswer) => void
}

export function FoundAtCounterPanel({ parcelId, find = findViaTrpc, onFound }: FoundAtCounterPanelProps) {
	const { t } = useTranslate()
	const [busy, setBusy] = useState(false)
	const [answer, setAnswer] = useState<FoundAtCounterAnswer | null>(null)
	const [error, setError] = useState<string | null>(null)

	const submit = async () => {
		setBusy(true)
		setError(null)
		try {
			const res = await find(parcelId)
			setAnswer(res)
			onFound?.(res)
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err))
		} finally {
			setBusy(false)
		}
	}

	return (
		<Alert data-testid='found-at-counter'>
			<SearchCheck />
			<AlertTitle className='flex items-center gap-2'>
				{t('foundParcel.title')}
				<Hint>{t('foundParcel.explain')}</Hint>
			</AlertTitle>
			{answer && (
				<AlertDescription className='text-foreground' role='status' data-testid='found-at-counter-answer'>
					{answer.where === 'own_point'
						? t('foundParcel.onShelf')
						: t('foundParcel.setAside', { point: answer.pointCode })}{' '}
					{answer.answerBy
						? t('foundParcel.awaiting', { at: formatDayTime(answer.answerBy) })
						: t('foundParcel.returnGoesBack')}
				</AlertDescription>
			)}
			{error && (
				<AlertDescription role='alert' className='text-destructive' data-testid='found-at-counter-error'>
					{t('foundParcel.failed', { message: error })}
				</AlertDescription>
			)}
			{!answer && (
				<AlertAction className='top-1.5'>
					<Button type='button' size='sm' disabled={busy} onClick={submit} data-testid='found-at-counter-submit'>
						{t('foundParcel.submit')}
					</Button>
				</AlertAction>
			)}
		</Alert>
	)
}
